import { ATTRIBUTION as OSM_ATTRIBUTION } from "ol/source/OSM";
import { useEffect, useMemo, useRef } from "react";
import { RLayerTile, useOL } from "rlayers";
import useClientConfig from "../../common/hooks/clientConfigHook";
import { useLayerManager } from "./LayerManager";
import { LeafletLayersControl } from '../leaflet/LeafletLayersControls';
import { LeafletTileLayer } from '../leaflet/LeafletTileLayer';
import { useTranslation } from "react-i18next";
import PropTypes from 'prop-types';
import { EsriJSON, WFS } from 'ol/format';
import { Image, Tile, Vector } from "ol/layer";
import { ImageStatic, TileArcGISRest, Vector as VectorSource } from "ol/source";
import { fromLonLat, transformExtent } from "ol/proj";
import { Fill, RegularShape, Stroke, Style } from "ol/style";
import { makeTileGrid } from "../../utils/mapUtilities";
import { fetch } from "../../common/utils/fetch";
import GML3 from "ol/format/GML3";
import {bbox as bboxStrategy} from 'ol/loadingstrategy';
import CircleStyle from "ol/style/Circle";

export const OLTileLayer = ({ layer, zIndex }) => {
    const { t } = useTranslation();
    const { registerLayer, unregisterLayer, isVisible, getOpacity } = useLayerManager();
    const id = 'layer_' + layer.title + (layer.group ? '_' + layer.group : '');

    useEffect(() => {
        registerLayer(id, layer.title, layer.group ?? t('overlay', { count: 100 }), 'Layer', layer.visible ?? false, layer.opacity ?? 1);
        return () => unregisterLayer(id);
    }, [id, layer, t, registerLayer, unregisterLayer]);

    const visible = isVisible(id);
    const opacity = getOpacity(id);

    const tileGrid = useMemo(() => {
        return makeTileGrid(layer.minZoom, layer.maxZoom);
    }, [layer.maxZoom, layer.minZoom]);

    return <RLayerTile tileGrid={tileGrid} properties={{ title: layer.title, group: layer.group, crossOrigin: 'anonymous' }} url={layer.url} crossOrigin="anonymous" visible={visible} opacity={opacity} attributions={layer.attributions} zIndex={zIndex}></RLayerTile>;
};

OLTileLayer.propTypes = {
    layer: PropTypes.object,
    zIndex: PropTypes.number
}

const kindMap = {
    'TileArcGISRest': TileArcGISRest
}

const makeSource = (map, layer) => {
    let extent = map.getView().calculateExtent(map.getSize());
    let latLonExtent = transformExtent(extent, 'EPSG:3857','EPSG:4326');
    let url = layer.options.url.replaceAll('{minx}',latLonExtent[0]);
    url = url.replaceAll('{miny}', latLonExtent[1]);
    url = url.replaceAll('{maxx}', latLonExtent[2]);
    url = url.replaceAll('{maxy}', latLonExtent[3]);
    url = url.replaceAll('{width}', Math.min(4096, document.getElementById('map')?.offsetWidth ?? 4096));
    url = url.replaceAll('{height}', Math.min(4096, document.getElementById('map')?.offsetHeight ?? 4096));
    return new ImageStatic({ url: url, imageExtent: extent })
}

const vectorMarkerStyles = {
    triangle: color => new Style({
        image: new RegularShape({
            fill: new Fill({color}),
            stroke: new Stroke({color, width: 1}),
            points: 3,
            radius: 16 / Math.sqrt(3), // Calculate the radius needed for a 16px side equilateral triangle
            rotation: 0, // Rotate the triangle so it points upwards
            angle: 0 // Initial angle
        })
    }),
    circle: color => new Style({
        image: new CircleStyle({
            radius: 3,
            fill: new Fill({ color }),
            stroke: new Stroke({ color, width: 2 }),
        }),
    })
}

export const DynamicLayer = ({ layer, zIndex }) => {
    const { t } = useTranslation();
    const { map } = useOL();
    const { registerLayer, unregisterLayer, isVisible, getOpacity } = useLayerManager();
    const layerRef = useRef();
    const id = 'layer_' + layer.title;

    const visible = isVisible(id);
    const opacity = getOpacity(id);

    useEffect(() => {
        registerLayer(id, layer.title, layer.group ?? t('overlay', { count: 100 }), 'Layer', layer.visible ?? true, layer.opacity ?? 1);
        return () => unregisterLayer(id);
    }, [id, layer, t, registerLayer, unregisterLayer]);

    useEffect(() => {
        if (!map) {
            return;
        }
        if (!layerRef.current) {
            switch (layer.type) {
                case null:
                case undefined:
                case 'tile': {
                    if (!kindMap[layer.kind]) {
                        throw new Error('Unknown tile kind ' + layer.kind);
                    }
                    layerRef.current = new Tile({ title: layer.title, visible: layer.visible ?? true, source: (new (kindMap[layer.kind])(layer.options))})
                    break;
                }
                case 'image': {
                    if (layer.options.extent) {
                        layerRef.current = new Image({ title: layer.title, visible: layer.visible ?? true, source: ImageStatic({ url: layer.options.url, imageExtent: fromLonLat(layer.options.extent.slice(0,2)).concat(fromLonLat(layer.options.extent.slice(2,4))) } )});
                    } else {
                        layerRef.current = new Image({ title: layer.title, visible: layer.visible ?? true, source: makeSource(map, layer) })
                    }
                    break;
                }
                case 'ArcGISFeature': {
                    const vectorSource = new VectorSource();
                    layerRef.current = new Vector({ title: layer.title, source: vectorSource, style: vectorMarkerStyles[layer.options.markerStyle](layer.options.color) });
                    fetch(layer.options.url).then(function(response) {
                        return response.json();
                    }).then(function(json) {
                        let features = new EsriJSON().readFeatures(json, {
                            featureProjection: 'EPSG:3857', // Specify the projection of the returned features
                            projection: 'EPSG:3857'
                        });
                        vectorSource.addFeatures(features);
                    });
                    break;
                }
                case 'WFS_GML3': {
                    const source = new VectorSource({
                        format: new WFS({ gmlFormat: new GML3() }),
                        url: function (extent) {
                            return `${layer.options.url}&bbox=${encodeURIComponent(extent.join(','))},EPSG:3857`
                        },
                        strategy: bboxStrategy
                    })
                    layerRef.current = new Vector({
                        source,
                        style: vectorMarkerStyles[layer.options.markerStyle](layer.options.color)
                    })
                    break;
                }
                default:
                    throw new Error('Uknown layer type ' + layer.type);
            }
        }
        if (zIndex) {
            layerRef.current.setZIndex(zIndex)
        }
        map.addLayer(layerRef.current);
        let interval = null;
        if (layer.refresh) {
            interval = setInterval(() => {
                if (layerRef.current?.getVisible()) {
                    layerRef.current?.getSource()?.refresh();
                }
            }, layer.refresh);
        }
        let viewListener = null;
        if (layer.type === 'image' && !layer.options?.extent) {
            viewListener = map.getView().on('change', () => {
                layerRef.current?.setSource?.(makeSource(map, layer));
            })
        }
        return () => {
            map.removeLayer(layerRef.current);
            if (interval !== null) {
                clearInterval(interval);
            }
            if (viewListener !== null) {
                map.getView().un('change', viewListener);
            }
        }
    }, [map, layer, zIndex]);

    useEffect(() => {
        if (layerRef.current && layerRef.current.getVisible() !== visible) {
            layerRef.current.setVisible(visible);
        }
    }, [visible]);

    useEffect(() => {
        if (layerRef.current && layerRef.current.getOpacity() !== opacity) {
            layerRef.current.setOpacity(opacity);
        }
    }, [opacity]);
}

export const Overlays = ({ mode, zIndex }) => {
    const clientConfig = useClientConfig();
    switch (mode) {
        case 'ol': {
            let overlays = [];
            if (clientConfig.customOverlays?.length > 0) {
                overlays = overlays.concat(clientConfig.customOverlays.map(layer => <OLTileLayer key={layer.title + (layer.group ? '_' + layer.group : '')} layer={layer} zIndex={zIndex}></OLTileLayer>));
            }
            if (clientConfig.mapTiles?.transportOverlays?.length > 0) {
                overlays = overlays.concat(clientConfig.mapTiles.transportOverlays.map(layer => <OLTileLayer key={layer.title + (layer.group ? '_' + layer.group : '')} layer={{ ...layer, group: layer.group ?? 'Transport', minZoom: 3, maxZoom: 19, attributions: [OSM_ATTRIBUTION] }} zIndex={zIndex + 5}></OLTileLayer>));
            }
            if (clientConfig.mapTiles?.layers?.length > 0) {
                overlays = overlays.concat(clientConfig.mapTiles.layers.map(layer => <DynamicLayer key={layer.title + (layer.group ? '_' + layer.group : '')} layer={layer} zIndex={zIndex}></DynamicLayer>));
            }
            return overlays;
        }
        case 'leaflet': {
            let overlays = [];
            if (clientConfig.customOverlays?.length > 0) {
                overlays = overlays.concat(clientConfig.customOverlays.filter(layer => layer.visible).map(layer => <LeafletLayersControl.Overlay key={layer.title} name={layer.title} checked={layer.visible}><LeafletTileLayer url={layer.url} crossOrigin="anonymous" opacity={layer.opacity} minZoom={layer.minZoom} maxZoom={layer.maxZoom} zIndex={zIndex}></LeafletTileLayer></LeafletLayersControl.Overlay>));
            }
            if (clientConfig.mapTiles?.transportOverlays?.length > 0) {
                overlays = overlays.concat(clientConfig.mapTiles.transportOverlays.map(layer => <LeafletLayersControl.Overlay key={layer.title} name={layer.title} checked={layer.visible}><LeafletTileLayer url={layer.url} crossOrigin="anonymous" opacity={layer.opacity ?? 0.7} minZoom={3} maxZoom={19} attribution={OSM_ATTRIBUTION} zIndex={zIndex + 5}></LeafletTileLayer></LeafletLayersControl.Overlay>));
            }
            return overlays;
        }
        default:
            throw new Error('Unknown mode ' + mode);
    }
};

Overlays.propTypes = {
    mode: PropTypes.oneOf(['ol','leaflet']),
    zIndex: PropTypes.number
}
